import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { clampFloatingMenuPosition } from '../utils/floatingMenuPosition';
import {
  ARRAY_INDEX_ALL,
  expandVariableValue,
  formatVariableRef,
  isArrayVariable,
  joinArrayValuesForPrint,
  normalizeArrayValues,
  normalizeVariableName,
} from '../../../utils/variableScope';
import { truncateMiddle } from '../utils/variableUtils';
import { VARIABLE_TYPE_CHOICES } from '../../../utils/variableTypeChange';
import { VariableTypeChoices } from './VariableTypeChoices';

/**
 * VariablePicker — opened by "{{" or the "/variable" slash command.
 *
 * Lists every variable visible from the doc's current context (doc-scoped
 * rows plus the campaign chain, highest-level-wins already applied by the host),
 * with value preview and source label. Array variables expand into one row
 * per index (`name[1]`, `name[2]`, …) plus `name[*]` (all values, one per line).
 * The bottom row creates a new doc-scoped variable from the typed name
 * after asking for Text / Number / Array.
 *
 * `onPick({ name, index })` — index is 1-based, `'*'` for all, or null.
 *
 * Same interaction model as DocMentionPicker: owns a search input; the
 * caret's spot in the paragraph was captured before focus moved here and is
 * restored on insert.
 */
const PREVIEW_MAX = 40;

const buildPickRows = (variables) => {
  const out = [];
  for (const v of variables || []) {
    if (isArrayVariable(v)) {
      const values = normalizeArrayValues(v.values);
      const allRaw = joinArrayValuesForPrint(values);
      const allExpanded = allRaw
        ? allRaw
            .split('\n')
            .map((line) => expandVariableValue(line, variables))
            .join('\n')
        : '';
      out.push({
        key: `${v.scope}-${v.sourceId || 'doc'}-${v.id || v.name}-*`,
        variable: v,
        name: v.name,
        index: ARRAY_INDEX_ALL,
        label: formatVariableRef(v.name, ARRAY_INDEX_ALL),
        expanded: allExpanded,
        preview: truncateMiddle(
          allExpanded ? allExpanded.replace(/\n/g, '↵') : '',
          PREVIEW_MAX
        ),
      });
      values.forEach((slot, i) => {
        const index = i + 1;
        const expanded = expandVariableValue(slot ?? '', variables);
        out.push({
          key: `${v.scope}-${v.sourceId || 'doc'}-${v.id || v.name}-${index}`,
          variable: v,
          name: v.name,
          index,
          label: formatVariableRef(v.name, index),
          expanded,
          preview: truncateMiddle(expanded, PREVIEW_MAX),
        });
      });
    } else {
      const expanded = expandVariableValue(v.value ?? '', variables);
      out.push({
        key: `${v.scope}-${v.sourceId || 'doc'}-${v.id || v.name}`,
        variable: v,
        name: v.name,
        index: null,
        label: v.name,
        expanded,
        preview: truncateMiddle(expanded, PREVIEW_MAX),
      });
    }
  }
  return out;
};

export const VariablePicker = ({ position, variables = [], onPick, onCreate, onClose }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [creatingName, setCreatingName] = useState('');
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const menuRef = useRef(null);
  const inputRef = useRef(null);

  const rows = useMemo(() => buildPickRows(variables), [variables]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      ({ label, expanded, variable: v }) =>
        label.toLowerCase().includes(q) ||
        v.name.toLowerCase().includes(q) ||
        expanded.toLowerCase().includes(q)
    );
  }, [rows, query]);

  const trimmedQuery = normalizeVariableName(query).replace(/\s+/g, '_');
  const nameTaken =
    !!trimmedQuery &&
    variables.some((v) => v.name.toLowerCase() === trimmedQuery.toLowerCase());
  const canCreate = !!onCreate && !!trimmedQuery && !nameTaken;
  const showCreateRow = !!onCreate;
  const createIndex = results.length;
  const optionCount = creatingName
    ? VARIABLE_TYPE_CHOICES.length
    : results.length + (showCreateRow ? 1 : 0);

  const beginCreate = () => {
    if (!canCreate) return;
    setCreatingName(trimmedQuery);
    setSelectedIndex(0);
  };

  const finishCreate = (type) => {
    if (!creatingName) return;
    onCreate(creatingName, type);
  };

  useEffect(() => setSelectedIndex(0), [query]);
  useEffect(() => {
    if (!trimmedQuery) setCreatingName('');
  }, [trimmedQuery]);

  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true });
  }, []);

  // Keep the menu inside the visible doc surface (resize only — scroll closes)
  useEffect(() => {
    const menu = menuRef.current;
    if (!menu) return undefined;
    const apply = () => setAdjustedPosition(clampFloatingMenuPosition(menu, position));
    apply();
    const raf = requestAnimationFrame(apply);
    const ro = new ResizeObserver(apply);
    ro.observe(menu);
    window.addEventListener('resize', apply);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', apply);
    };
  }, [position, results, showCreateRow, creatingName]);

  // Page/doc scroll (incl. scrollbar drag) dismisses — scrolling the menu itself does not
  useEffect(() => {
    const onScroll = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      onClose();
    };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [onClose]);

  const pickRow = (row) => {
    if (!row) return;
    onPick({ name: row.name, index: row.index });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (optionCount ? (prev + 1) % optionCount : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (optionCount ? (prev - 1 + optionCount) % optionCount : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (creatingName) {
        const choice = VARIABLE_TYPE_CHOICES[selectedIndex];
        if (choice) finishCreate(choice.type);
        return;
      }
      if (results[selectedIndex]) pickRow(results[selectedIndex]);
      else if (showCreateRow && selectedIndex === createIndex && canCreate) beginCreate();
    } else if (e.key === 'Backspace' && creatingName && !query) {
      e.preventDefault();
      setCreatingName('');
    }
  };

  // Capture phase — wins over doc-sheet / mission Esc handlers
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      if (creatingName) {
        setCreatingName('');
        return;
      }
      onClose();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onClose, creatingName]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const stateOf = (expanded) => (String(expanded ?? '').trim() ? 'resolved' : 'empty');

  return createPortal(
    <div
      ref={menuRef}
      className="doc-slash-menu doc-mention-picker doc-variable-picker"
      style={{ top: adjustedPosition.y, left: adjustedPosition.x }}
    >
      <input
        ref={inputRef}
        className="doc-mention-picker__search"
        value={query}
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
        lang="zxx"
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Search variables…"
        aria-label="Search variables"
      />

      {creatingName ? (
        <VariableTypeChoices
          name={creatingName}
          selectedIndex={selectedIndex}
          onChoose={finishCreate}
          onHover={setSelectedIndex}
        />
      ) : results.map((row, index) => (
        <div
          key={row.key}
          className={`doc-slash-item ${index === selectedIndex ? 'selected' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            pickRow(row);
          }}
          onMouseEnter={() => setSelectedIndex(index)}
          title={row.expanded.trim() ? row.expanded : undefined}
        >
          <span
            className="doc-variable-picker__dot"
            data-var-state={stateOf(row.expanded)}
            style={row.variable.color ? { '--var-color': row.variable.color } : undefined}
          />
          <span className="doc-slash-label">{row.label}</span>
          <span className="doc-slash-description">
            · {row.expanded.trim() ? row.preview : 'no value yet'}
            {row.variable.sourceName && row.variable.scope !== 'doc'
              ? ` — ${row.variable.sourceName}`
              : ''}
          </span>
        </div>
      ))}

      {!creatingName && results.length === 0 && !showCreateRow && (
        <div className="doc-slash-empty">
          {variables.length === 0 ? 'No variables in scope' : 'No matches'}
        </div>
      )}

      {!creatingName && showCreateRow && (
        <div
          className={`doc-slash-item doc-slash-item--create${
            !canCreate ? ' doc-slash-item--create-hint' : ''
          } ${selectedIndex === createIndex && canCreate ? 'selected' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            if (canCreate) beginCreate();
          }}
          onMouseEnter={() => canCreate && setSelectedIndex(createIndex)}
        >
          <span className="doc-slash-icon">+</span>
          <span className="doc-slash-label">
            Create New:
            {trimmedQuery ? (
              <span className="doc-slash-item__create-name"> {trimmedQuery}</span>
            ) : (
              <span className="doc-slash-item__create-placeholder"> type a name above</span>
            )}
          </span>
        </div>
      )}

      {!creatingName && nameTaken && (
        <div className="doc-slash-empty">
          &ldquo;{trimmedQuery}&rdquo; already exists — pick it from the list
        </div>
      )}

      <div className="doc-mention-picker__hint">
        <span>↑↓ choose</span>
        <span>Enter insert</span>
        <span>Esc cancel</span>
      </div>
    </div>,
    document.body
  );
};
